/**
 * Utility for managing table state in localStorage
 * Handles pagination and column selection persistence
 * Organized by workspace and user to persist across logouts
 */

const TABLE_STORAGE_KEY = 'table_configuration';

/**
 * Get current user and workspace IDs from localStorage
 * @returns {Object} Object containing workspaceId and userId
 */
const getCurrentContext = () => {
  try {
    const userData = sessionStorage.getItem('userData');
    if (!userData) {
      return { workspaceId: null, userId: null };
    }

    const parsedData = JSON.parse(userData);

    const workspaceId = parsedData?.activeWorkspace || null;
    const userId = parsedData?.id || null;

    return { workspaceId, userId };
  } catch (error) {
    console.error('tableStorage: Failed to get current context', error);
    return { workspaceId: null, userId: null };
  }
};

/**
 * Get all table states from persistent storage
 * @returns {Object} All table states organized by workspace and user
 */
const getAllStates = () => {
  try {
    const stored = localStorage.getItem(TABLE_STORAGE_KEY);
    if (!stored) {
      return {};
    }
    return JSON.parse(stored);
  } catch (error) {
    console.error('tableStorage: Failed to get all states', error);
    return {};
  }
};

/**
 * Save all table states to persistent storage
 * @param {Object} states - All table states to save
 */
const saveAllStates = (states) => {
  try {
    localStorage.setItem(TABLE_STORAGE_KEY, JSON.stringify(states));
  } catch (error) {
    console.error('tableStorage: Failed to save all states', error);
  }
};

/**
 * Save table state to localStorage
 * @param {string} tableId - Unique identifier for the table
 * @param {Object} state - Table state to save
 * @param {number} state.currentPage - Current page number
 * @param {number} state.limit - Items per page
 * @param {Array} state.columns - Column visibility configuration
 */
export const saveTableState = (tableId, state) => {
  if (!tableId) {
    return;
  }

  try {
    const { workspaceId, userId } = getCurrentContext();
    
    if (!workspaceId || !userId) {
      return;
    }

    const allStates = getAllStates();
    
    // Initialize nested structure if needed
    if (!allStates[workspaceId]) {
      allStates[workspaceId] = {};
    }
    if (!allStates[workspaceId][userId]) {
      allStates[workspaceId][userId] = {};
    }

    const existingState = allStates[workspaceId][userId][tableId] || {};
    
    const newState = {
      ...existingState,
      ...state,
      lastUpdated: new Date().toISOString(),
    };

    allStates[workspaceId][userId][tableId] = newState;
    saveAllStates(allStates);
  } catch (error) {
    console.error('tableStorage: Failed to save table state', error);
  }
};

/**
 * Get table state from localStorage
 * @param {string} tableId - Unique identifier for the table
 * @returns {Object|null} Saved table state or null if not found
 */
export const getTableState = (tableId) => {
  if (!tableId) {
    console.warn('tableStorage: tableId is required to get state');
    return null;
  }

  try {
    const { workspaceId, userId } = getCurrentContext();
    
    if (!workspaceId || !userId) {
      return null;
    }

    const allStates = getAllStates();
    
    if (!allStates[workspaceId] || !allStates[workspaceId][userId]) {
      return null;
    }

    return allStates[workspaceId][userId][tableId] || null;
  } catch (error) {
    console.error('tableStorage: Failed to get table state', error);
    return null;
  }
};

/**
 * Save pagination state
 * @param {string} tableId - Unique identifier for the table
 * @param {number} currentPage - Current page number
 * @param {number} limit - Items per page
 */
export const savePaginationState = (tableId, currentPage, limit) => {
  saveTableState(tableId, { currentPage, limit });
};

/**
 * Get pagination state
 * @param {string} tableId - Unique identifier for the table
 * @returns {Object|null} Pagination state { currentPage, limit } or null
 */
export const getPaginationState = (tableId) => {
  const state = getTableState(tableId);
  
  if (!state || (!state.currentPage && !state.limit)) {
    return null;
  }

  return {
    currentPage: state.currentPage || 1,
    limit: state.limit || 10,
  };
};

/**
 * Save card view state
 * @param {string} tableId - Unique identifier for the table
 * @param {string} cardView - View mode ('table' or 'card')
 */
export const saveCardViewState = (tableId, cardView) => {
  if (!cardView) {
    console.warn('tableStorage: cardView is required');
    return;
  }

  saveTableState(tableId, { cardView });
};

/**
 * Get card view state
 * @param {string} tableId - Unique identifier for the table
 * @returns {string|null} Card view state ('table' or 'card') or null
 */
export const getCardViewState = (tableId) => {
  const state = getTableState(tableId);
  
  if (!state || !state.cardView) {
    return null;
  }

  return state.cardView;
};

/**
 * Save column selection state
 * @param {string} tableId - Unique identifier for the table
 * @param {Array} columns - Array of column objects with visibility state
 */
export const saveColumnState = (tableId, columns) => {
  if (!Array.isArray(columns)) {
    console.warn('tableStorage: columns must be an array');
    return;
  }

  // Only save sortKey and isVisible to keep storage minimal
  const columnState = columns.map(col => ({
    sortKey: col.sortKey,
    isVisible: col.isVisible,
  }));

  saveTableState(tableId, { columns: columnState });
};

/**
 * Get column selection state
 * @param {string} tableId - Unique identifier for the table
 * @returns {Array|null} Array of column states or null
 */
export const getColumnState = (tableId) => {
  const state = getTableState(tableId);
  
  if (!state || !Array.isArray(state.columns)) {
    return null;
  }

  return state.columns;
};

/**
 * Save sort order state
 * @param {string} tableId - Unique identifier for the table
 * @param {Object} sort - Sort configuration
 * @param {string} sort.key - Sort key/column name
 * @param {string} sort.direction - Sort direction ('asc' or 'desc')
 */
export const saveSortState = (tableId, sort) => {
  if (!sort || !sort.key) {
    console.warn('tableStorage: sort object with key is required');
    return;
  }

  saveTableState(tableId, { 
    sort: {
      key: sort.key,
      direction: sort.direction || 'asc',
    }
  });
};

/**
 * Get sort order state
 * @param {string} tableId - Unique identifier for the table
 * @returns {Object|null} Sort state { key, direction } or null
 */
export const getSortState = (tableId) => {
  const state = getTableState(tableId);
  
  if (!state || !state.sort) {
    return null;
  }

  return {
    key: state.sort.key,
    direction: state.sort.direction || 'asc',
  };
};

/**
 * Clear table state from localStorage
 * @param {string} tableId - Unique identifier for the table
 */
export const clearTableState = (tableId) => {
  if (!tableId) {
    console.warn('tableStorage: tableId is required to clear state');
    return;
  }

  try {
    const { workspaceId, userId } = getCurrentContext();
    
    if (!workspaceId || !userId) {
      console.warn('tableStorage: workspaceId and userId are required to clear state');
      return;
    }

    const allStates = getAllStates();
    
    if (allStates[workspaceId] && allStates[workspaceId][userId]) {
      delete allStates[workspaceId][userId][tableId];
      saveAllStates(allStates);
    }
  } catch (error) {
    console.error('tableStorage: Failed to clear table state', error);
  }
};

/**
 * Clear all table states for current user in current workspace
 */
export const clearAllTableStates = () => {
  try {
    const { workspaceId, userId } = getCurrentContext();
    
    if (!workspaceId || !userId) {
      console.warn('tableStorage: workspaceId and userId are required to clear all states');
      return;
    }

    const allStates = getAllStates();
    
    if (allStates[workspaceId] && allStates[workspaceId][userId]) {
      allStates[workspaceId][userId] = {};
      saveAllStates(allStates);
    }
  } catch (error) {
    console.error('tableStorage: Failed to clear all table states', error);
  }
};

/**
 * Clear all table states across all workspaces and users (use with caution)
 */
export const clearAllTableStatesGlobal = () => {
  try {
    localStorage.removeItem(TABLE_STORAGE_KEY);
  } catch (error) {
    console.error('tableStorage: Failed to clear all global table states', error);
  }
};
